import React from 'react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartConfig,
} from './ui/chart';
import {
  LineChart as RechartsLineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import dayjs from 'dayjs';

export interface LineChartData {
  date: string;
  value: number;
  [key: string]: any;
}

export interface LineChartProps {
  data: LineChartData[];
  title: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  valueKey?: string;
  dateKey?: string;
  yMin?: number;
  yMax?: number;
  formatValue?: (value: number) => string;
  color?: string;
}

const defaultFormatters = {
  currency: (value: number) => `R$ ${(value / 100).toFixed(2)}`,
  percent: (value: number) => `${value.toFixed(1)}%`,
  integer: (value: number) => value.toLocaleString('pt-BR'),
  default: (value: number) => value.toLocaleString('pt-BR'),
};

export function LineChart({
  data,
  title,
  xAxisLabel = 'Data',
  yAxisLabel = 'Valor',
  valueKey = 'value',
  dateKey = 'date',
  yMin,
  yMax,
  formatValue = defaultFormatters.default,
  color = 'hsl(var(--primary))',
}: LineChartProps) {
  const chartConfig: ChartConfig = {
    [valueKey]: {
      label: yAxisLabel,
    },
  };

  // Transform data to include formatted dates for display
  const transformedData = data.map((item) => ({
    ...item,
    displayDate: dayjs(item[dateKey]).format('DD/MM'),
    [valueKey]: item[valueKey],
  }));

  return (
    <div className="w-full">
      <h3 className="text-lg font-semibold mb-4 text-center">{title}</h3>
      <ChartContainer config={chartConfig} className="h-[300px] w-full">
        <RechartsLineChart
          data={transformedData}
          margin={{
            left: 20,
            right: 20,
            top: 20,
            bottom: 20,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="displayDate"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
          />
          <YAxis
            domain={yMin !== undefined && yMax !== undefined ? [yMin, yMax] : undefined}
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            tick={{ fontSize: 12 }}
            tickFormatter={formatValue}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    const date = payload[0]?.payload?.[dateKey];
                    return date ? dayjs(date).format('DD/MM/YYYY') : label;
                  }
                  return label;
                }}
                formatter={(value, name) => [
                  formatValue(value as number),
                  yAxisLabel,
                ]}
              />
            }
          />
          <Line
            type="monotone"
            dataKey={valueKey}
            stroke={color}
            strokeWidth={2}
            dot={{ r: 4, fill: color }}
            activeDot={{ r: 6, fill: color }}
          />
        </RechartsLineChart>
      </ChartContainer>
    </div>
  );
}

// Specialized chart variants for common use cases
export function CurrencyLineChart(props: Omit<LineChartProps, 'formatValue'>) {
  return (
    <LineChart
      {...props}
      formatValue={defaultFormatters.currency}
    />
  );
}

export function PercentLineChart(props: Omit<LineChartProps, 'formatValue'>) {
  return (
    <LineChart
      {...props}
      formatValue={defaultFormatters.percent}
    />
  );
}

export function IntegerLineChart(props: Omit<LineChartProps, 'formatValue'>) {
  return (
    <LineChart
      {...props}
      formatValue={defaultFormatters.integer}
    />
  );
}